import React, { useState, ReactNode, useRef } from 'react';
import { createPortal } from 'react-dom';

interface TooltipProps {
  children: ReactNode;
  content: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
  className?: string;
}

export const Tooltip: React.FC<TooltipProps> = ({
  children,
  content,
  position = 'top',
  className = '',
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);

  const getTooltipStyle = (): React.CSSProperties => {
    if (!triggerRef.current) {
      return { position: 'fixed', zIndex: 99999 };
    }

    const rect = triggerRef.current.getBoundingClientRect();
    const style: React.CSSProperties = {
      position: 'fixed',
      zIndex: 99999,
    };

    switch (position) {
      case 'top':
        style.left = rect.left + rect.width / 2;
        style.top = rect.top - 6;
        style.transform = 'translate(-50%, -100%)';
        break;
      case 'bottom':
        style.left = rect.left + rect.width / 2;
        style.top = rect.bottom + 6;
        style.transform = 'translate(-50%, 0)';
        break;
      case 'left':
        style.left = rect.left - 6;
        style.top = rect.top + rect.height / 2;
        style.transform = 'translate(-100%, -50%)';
        break;
      case 'right':
        style.left = rect.right + 6;
        style.top = rect.top + rect.height / 2;
        style.transform = 'translate(0, -50%)';
        break;
    }

    return style;
  };

  const getArrowStyle = (): { container: React.CSSProperties; inner: React.CSSProperties } => {
    const base: React.CSSProperties = {
      position: 'absolute',
      width: 10,
      height: 10,
      overflow: 'hidden',
    };
    const inner: React.CSSProperties = {
      position: 'absolute',
      width: 7,
      height: 7,
      transform: 'rotate(45deg)',
    };

    switch (position) {
      case 'top':
        return {
          container: { ...base, bottom: -5, left: '50%', marginLeft: -5 },
          inner: { ...inner, top: -4, left: 1.5 },
        };
      case 'bottom':
        return {
          container: { ...base, top: -5, left: '50%', marginLeft: -5 },
          inner: { ...inner, bottom: -4, left: 1.5 },
        };
      case 'left':
        return {
          container: { ...base, right: -5, top: '50%', marginTop: -5 },
          inner: { ...inner, left: -4, top: 1.5 },
        };
      case 'right':
        return {
          container: { ...base, left: -5, top: '50%', marginTop: -5 },
          inner: { ...inner, right: -4, top: 1.5 },
        };
      default:
        return { container: base, inner };
    }
  };

  const arrowStyle = getArrowStyle();

  return (
    <div
      ref={triggerRef}
      className={`relative inline-block ${className}`}
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
      onFocus={() => setIsVisible(true)}
      onBlur={() => setIsVisible(false)}
    >
      {children}
      {isVisible &&
        createPortal(
          <div
            style={getTooltipStyle()}
            className="animate-in fade-in-0 zoom-in-95 duration-100 px-3 py-2 text-xs text-foreground/90 bg-background backdrop-blur-md border border-foreground/[0.06] rounded-md shadow-md shadow-black/8 w-64 max-w-xs whitespace-normal leading-relaxed tracking-normal"
          >
            {content}
            <div style={arrowStyle.container}>
              <div style={arrowStyle.inner} className="bg-background border border-foreground/[0.06]" />
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};
